import { useCanWrite } from '@hooks/useCanWrite';
import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Crew } from '@models/crew.model';
import { Employee } from '@models/employee.model';
import { SecondarySidebar } from '@components/organisms/SecondarySidebar';
import UserPunchHistoryDrawer from '@components/organisms/UserPunchHistoryDrawer';
import { CrewHeader } from './components/CrewHeader';
import { Loading } from '@components/atoms/Loading';
import { CrewMembers } from './components/CrewMembers';
import { CrewAssignments } from './components/CrewAssignments';
import { useRememberedSelection } from '@hooks/useRememberedSelection';
import { useCrews } from '@hooks/api/crew/useCrews';
import { useCreateCrew } from '@hooks/api/crew/useCreateCrew';
import { useEditCrew } from '@hooks/api/crew/useEditCrew';
import { useCrewUnassignEmployee } from '@hooks/api/crew/useCrewUnassignEmployee';
import { CreateEditCrewModal } from './components/CreateEditCrewModal';
import { ConfirmationModal } from '@components/molecules/ConfirmationModal';
import { ManageCrewMembersModal } from './components/ManageCrewMembersModal';
import { ManageCrewTasksModal } from './components/ManageCrewTasksModal';
import { EmptyData } from '@components/molecules/EmptyData';
import AddIcon from '@mui/icons-material/Add';

interface RouteParams {
  id: string;
}

const CrewsPage: React.FC = () => {
  // Covers both causes of a locked account: a read-only membership, and an organization
  // whose trial has expired.
  const { canWrite } = useCanWrite();
  const isReadOnly = !canWrite;
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showManageCrewMembersModal, setShowManageCrewMembersModal] =
    useState<boolean>(false);
  const [showManageCrewTasksModal, setShowManageCrewTasksModal] =
    useState<boolean>(false);
  const [showUnassignModal, setShowUnassignModal] = useState<boolean>(false);
  const [employeeToUnassign, setEmployeeToUnassign] = useState<string | null>(
    null,
  );
  const [historyEmployee, setHistoryEmployee] = useState<Employee | null>(null);
  const { id } = useParams<RouteParams>();
  const [showNewCrewModal, setShowNewCrewModal] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<Crew | null>(null);
  const [initialData, setInitialData] = useState<any | null>(null);
  const history = useHistory();
  const { mutateAsync: createCrew, isPending: isCreating } = useCreateCrew();
  const { mutate: editCrew, isPending: isEditing } = useEditCrew();
  const { mutate: unassignEmployee, isPending: isUnassigning } =
    useCrewUnassignEmployee();

  const { data: crews, isLoading: crewsLoading } = useCrews();

  // Find the selected crew from the crews list instead of making a separate API call
  const crew = React.useMemo(() => {
    if (!id || !crews) return null;
    return crews.find((c) => c.crewId === id) || null;
  }, [id, crews]);

  const { onSelect: onRememberedSelect } = useRememberedSelection<Crew>({
    key: 'crew',
    items: crews || [],
    getId: (c) => c.crewId,
    basePath: '/crews',
  });

  useEffect(() => {
    if (id) {
      onRememberedSelect(id);
    }
  }, [id, onRememberedSelect]);

  const handleSubmitCrew = async (values: { name: string }) => {
    if (initialData) {
      editCrew({ crewId: initialData.crewId, name: values.name });
    } else {
      try {
        const result = await createCrew(values.name);
        // Navigate to the newly created crew
        if (result?.crewId) {
          onRememberedSelect(result.crewId);
          history.push(`/crews/${result.crewId}`);
        }
      } catch (error) {
        console.error('Error creating crew:', error);
        return; // Don't close modal if there's an error
      }
    }
    toggleNewCrewModal();
  };

  const toggleNewCrewModal = () => {
    setShowNewCrewModal(!showNewCrewModal);
    setInitialData(null);
  };

  const handleMenuItemClick = (item: any) => {
    const crew: Crew = item?.crew ?? item;
    history.push(`/crews/${crew.crewId}`);
    setSelectedItem(crew);
  };

  const handleConfirmDeleteCrew = () => {
    setShowDeleteModal(!showDeleteModal);
  };

  const handleEditCrew = () => {
    setInitialData(crew);
    setShowNewCrewModal(true);
  };

  const toggleManageCrewMembersModal = () => {
    setShowManageCrewMembersModal(!showManageCrewMembersModal);
  };

  const toggleManageCrewTasksModal = () => {
    setShowManageCrewTasksModal(!showManageCrewTasksModal);
  };

  const handleUnassignEmployee = (employeeId: string) => {
    setEmployeeToUnassign(employeeId);
    setShowUnassignModal(true);
  };

  const handleConfirmUnassignEmployee = () => {
    if (employeeToUnassign && id) {
      unassignEmployee({
        crewId: id,
        employeeId: employeeToUnassign,
      });
    }
    setShowUnassignModal(false);
    setEmployeeToUnassign(null);
  };

  const handleCancelUnassignEmployee = () => {
    if (isUnassigning) return; // Prevent closing while unassigning
    setShowUnassignModal(false);
    setEmployeeToUnassign(null);
  };

  const isBusy = crewsLoading;
  const currentCrew: Crew | null =
    (id ? (crew as Crew | undefined) : selectedItem) || null;

  return (
    <div className="flex flex-col min-h-full gap-6">
      <SecondarySidebar
        selectedItem={id || currentCrew?.crewId}
        addButtonLabel="New Crew"
        items={(crews || []).map((crew) => ({
          id: crew.crewId,
          name: crew?.name,
          crew,
        }))}
        toggleModal={toggleNewCrewModal}
        handleMenuItemClick={handleMenuItemClick}
        disabled={isReadOnly}
      />

      {!crewsLoading && crews?.length === 0 && (
        <div className="h-full">
          <EmptyData
            textButton="New Crew"
            onClickButton={toggleNewCrewModal}
            description="No Active Crews Yet"
            iconButton={<AddIcon sx={{ fontSize: '16px' }} />}
            disabled={isReadOnly}
          />
        </div>
      )}

      {isBusy ? (
        <Loading center size="2xl" />
      ) : (
        <>
          {/* Natural height, not h-full: the assignments panel grows with the
              task table, and the layout's own scroll container handles it.
              Pinning to viewport height clipped everything past the fold. */}
          {crews && crews?.length > 0 && (
            <div className="flex flex-col">
              <CrewHeader
                onEdit={handleEditCrew}
                crew={currentCrew as Crew}
                onDelete={handleConfirmDeleteCrew}
              />
              <CrewMembers
                onRemoveMember={handleUnassignEmployee}
                onSelectMember={(employee) => setHistoryEmployee(employee)}
                employees={currentCrew?.employees || []}
                onManageMembers={toggleManageCrewMembersModal}
              />
              <CrewAssignments
                tasks={currentCrew?.tasks || []}
                onManageTasks={toggleManageCrewTasksModal}
                isReadOnly={isReadOnly}
              />
            </div>
          )}
        </>
      )}

      <CreateEditCrewModal
        isOpen={showNewCrewModal}
        onSubmit={handleSubmitCrew}
        onClose={toggleNewCrewModal}
        initialName={initialData?.name}
        isSubmitting={isCreating || isEditing}
      />

      <ConfirmationModal
        variant="info"
        title="Delete Crew"
        isOpen={showDeleteModal}
        onClose={handleConfirmDeleteCrew}
        onConfirm={handleConfirmDeleteCrew}
        description="If you need to delete a crew, please contact with the owner of the project."
      />

      <ManageCrewMembersModal
        crewId={id}
        crew={currentCrew}
        isOpen={showManageCrewMembersModal}
        onClose={toggleManageCrewMembersModal}
      />

      <ManageCrewTasksModal
        crewId={id}
        crew={currentCrew}
        isOpen={showManageCrewTasksModal}
        onClose={toggleManageCrewTasksModal}
      />

      <UserPunchHistoryDrawer
        isOpen={!!historyEmployee}
        onClose={() => setHistoryEmployee(null)}
        employee={historyEmployee}
      />

      <ConfirmationModal
        variant="danger"
        title="Remove Crew Member"
        isOpen={showUnassignModal}
        onClose={handleCancelUnassignEmployee}
        onConfirm={handleConfirmUnassignEmployee}
        description="Are you sure you want to remove this employee from the crew? This action cannot be undone."
        confirmText={isUnassigning ? 'Removing...' : 'Remove'}
        closeOnOverlayClick={!isUnassigning}
        closeOnEscape={!isUnassigning}
      />
    </div>
  );
};

export default CrewsPage;
